/**
 * SuperAdminCommandCenter.js
 * ---------------------------------------------------------------
 * Phase K — support + revenue panels wired into SuperAdminDashboard.
 *
 * This file bundles every new UI surface added in Phase K so the huge
 * SuperAdminDashboard.js stays roughly the same shape and only needs
 * to import + mount these exports.
 *
 * Exports:
 *   - GlobalSearchBar         (top-bar search, cross-collection)
 *   - RevenueTab              ("Revenue" left-nav tab)
 *   - AuditLogTab             ("Audit Log" left-nav tab)
 *   - EmailHealthTab          ("Email Health" left-nav tab)
 *   - OrgBillingPanel         (embedded in org detail modal)
 *   - OrgUsagePanel           (embedded in org detail modal)
 *   - OrgQuickActionsBar      (embedded in org detail modal)
 *   - ImpersonationBanner     (mounted globally)
 *   - startImpersonation      (helper — swaps localStorage tokens)
 *   - IMPERSONATION_KEY       (localStorage bookkeeping keys)
 *   - IMPERSONATION_BACKUP_KEY
 *
 * Styling: keeps the plain-CSS conventions used by SuperAdminDashboard.js
 * (className="card / stat-card / btn / badge") for zero visual friction.
 * Every interactive element has a `data-testid`.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../utils/api';

// ---------------------------------------------------------------
// Impersonation helpers (localStorage-driven)
// ---------------------------------------------------------------
export const IMPERSONATION_KEY = 'kw_impersonation_meta';
export const IMPERSONATION_BACKUP_KEY = 'kw_impersonation_backup';

/**
 * Kick off impersonation for a target user id.
 *   1. Requests a 2h-scoped JWT from the backend.
 *   2. Backs up the super admin's current token + user object.
 *   3. Swaps `token` + `user` in localStorage to the target's session.
 *   4. Reloads the page so the whole app re-hydrates as the target.
 * The banner (mounted globally) reads `IMPERSONATION_KEY` to know we're
 * in an impersonated session.
 */
export async function startImpersonation(userId) {
  const res = await api.superAdminImpersonate(userId);
  // Back up the current super-admin token/user so "Exit" is one click.
  try {
    localStorage.setItem(
      IMPERSONATION_BACKUP_KEY,
      JSON.stringify({
        token: localStorage.getItem('token'),
        user: localStorage.getItem('user'),
      })
    );
  } catch (_) {
    /* ignore quota errors — Exit button will still work if backup exists */
  }
  localStorage.setItem(
    IMPERSONATION_KEY,
    JSON.stringify({
      started_at: new Date().toISOString(),
      target_email: res.user?.email,
      target_org_id: res.user?.organization_id,
      target_org_name: res.user?.organization_name,
      impersonated_by_email: res.impersonated_by?.email,
      expires_at: new Date(Date.now() + (res.expires_in_seconds || 7200) * 1000).toISOString(),
    })
  );
  localStorage.setItem('token', res.access_token);
  localStorage.setItem('user', JSON.stringify(res.user));
  window.location.assign('/'); // route guards will send them to the right dashboard
}

function stopImpersonation() {
  try {
    const raw = localStorage.getItem(IMPERSONATION_BACKUP_KEY);
    if (raw) {
      const backup = JSON.parse(raw);
      if (backup?.token) localStorage.setItem('token', backup.token);
      if (backup?.user) localStorage.setItem('user', backup.user);
    }
  } catch (_) { /* fall through */ }
  localStorage.removeItem(IMPERSONATION_KEY);
  localStorage.removeItem(IMPERSONATION_BACKUP_KEY);
  window.location.assign('/super-admin');
}

// ---------------------------------------------------------------
// ImpersonationBanner — mounted at the app root, only renders when
// there's an active impersonation session in localStorage.
// ---------------------------------------------------------------
export function ImpersonationBanner() {
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    const read = () => {
      try {
        const raw = localStorage.getItem(IMPERSONATION_KEY);
        setMeta(raw ? JSON.parse(raw) : null);
      } catch (_) {
        setMeta(null);
      }
    };
    read();
    // Refresh when other tabs update it, or every 30s (token expiry check).
    window.addEventListener('storage', read);
    const iv = setInterval(read, 30_000);
    return () => {
      window.removeEventListener('storage', read);
      clearInterval(iv);
    };
  }, []);

  if (!meta) return null;
  return (
    <div
      data-testid="impersonation-banner"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 200,
        background: 'linear-gradient(90deg, hsl(28 90% 55%), hsl(12 88% 55%))',
        color: 'white',
        padding: '0.6rem 1rem',
        display: 'flex',
        gap: '0.75rem',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        fontSize: '0.9rem',
        fontWeight: 600,
      }}
    >
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <span style={{ fontSize: '1.1rem' }}>🎭</span>
        <span>
          Impersonating <code style={{ background: 'rgba(0,0,0,0.15)', padding: '0.1rem 0.35rem', borderRadius: 4 }}>{meta.target_email}</code>
          {meta.target_org_name ? <> at <strong>{meta.target_org_name}</strong></> : null}
        </span>
      </div>
      <button
        type="button"
        onClick={stopImpersonation}
        data-testid="exit-impersonation-btn"
        style={{
          background: 'rgba(0,0,0,0.25)',
          border: '1px solid rgba(255,255,255,0.4)',
          color: 'white',
          padding: '0.35rem 0.85rem',
          borderRadius: 6,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        🚪 Exit impersonation
      </button>
    </div>
  );
}

// ---------------------------------------------------------------
// GlobalSearchBar — top-of-header combobox
// ---------------------------------------------------------------
export function GlobalSearchBar({ onOpenOrg }) {
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const runSearch = useCallback(async (text) => {
    if ((text || '').trim().length < 2) {
      setResults(null);
      return;
    }
    setBusy(true);
    try {
      const r = await api.superAdminGlobalSearch(text.trim());
      setResults(r);
    } catch (err) {
      setResults({ error: err.message });
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => runSearch(q), 250);
    return () => clearTimeout(timer.current);
  }, [q, runSearch]);

  const hasResults = results && !results.error && (
    (results.organizations || []).length + (results.users || []).length + (results.children || []).length > 0
  );

  return (
    <div
      style={{ position: 'relative', flex: 1, maxWidth: 520 }}
      data-testid="global-search-container"
      onFocus={() => setOpen(true)}
      onBlur={(e) => {
        // Delay so a click on a result registers first.
        setTimeout(() => {
          if (!e.currentTarget.contains(document.activeElement)) setOpen(false);
        }, 150);
      }}
    >
      <input
        type="search"
        placeholder="🔎 Search orgs, users, children by name/email/ID…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => setOpen(true)}
        data-testid="global-search-input"
        style={{
          width: '100%',
          padding: '0.55rem 0.9rem',
          borderRadius: 999,
          border: '1px solid hsl(var(--border))',
          background: 'hsl(var(--background))',
          fontSize: '0.9rem',
        }}
      />
      {open && q.trim().length >= 2 && (
        <div
          data-testid="global-search-results"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            background: 'hsl(var(--background))',
            border: '1px solid hsl(var(--border))',
            borderRadius: 8,
            maxHeight: 420,
            overflowY: 'auto',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            zIndex: 50,
          }}
        >
          {busy ? (
            <div style={{ padding: '0.75rem 1rem', color: 'hsl(var(--muted-foreground))' }}>Searching…</div>
          ) : results?.error ? (
            <div style={{ padding: '0.75rem 1rem', color: 'hsl(0 70% 50%)' }}>{results.error}</div>
          ) : !hasResults ? (
            <div style={{ padding: '0.75rem 1rem', color: 'hsl(var(--muted-foreground))' }}>No matches</div>
          ) : (
            <>
              {(results.organizations || []).length > 0 && (
                <div>
                  <div style={sectionLabel}>Organizations</div>
                  {results.organizations.map((o) => (
                    <button
                      key={o._id}
                      type="button"
                      onMouseDown={() => { onOpenOrg && onOpenOrg(o); setOpen(false); setQ(''); }}
                      data-testid={`gs-org-${o._id}`}
                      style={resultRowStyle}
                    >
                      <div style={{ fontWeight: 600 }}>🏢 {o.name}</div>
                      <div style={subtleStyle}>
                        <code>{o.slug}</code> · {o.contact_email || 'no email'}
                        {o.is_active === false ? <span style={{ color: 'hsl(0 70% 50%)', marginLeft: 6 }}>· SUSPENDED</span> : null}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {(results.users || []).length > 0 && (
                <div>
                  <div style={sectionLabel}>Users</div>
                  {results.users.map((u) => (
                    <div key={u._id} style={{ ...resultRowStyle, cursor: 'default' }} data-testid={`gs-user-${u._id}`}>
                      <div style={{ fontWeight: 600 }}>👤 {u.full_name || u.email}</div>
                      <div style={subtleStyle}>{u.email} · {u.role} · org <code>{u.organization_id || '—'}</code></div>
                    </div>
                  ))}
                </div>
              )}
              {(results.children || []).length > 0 && (
                <div>
                  <div style={sectionLabel}>Children</div>
                  {results.children.map((c) => (
                    <div key={c._id} style={{ ...resultRowStyle, cursor: 'default' }} data-testid={`gs-child-${c._id}`}>
                      <div style={{ fontWeight: 600 }}>👶 {c.first_name} {c.last_name}</div>
                      <div style={subtleStyle}>org <code>{c.organization_id}</code></div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const sectionLabel = {
  padding: '0.4rem 0.9rem',
  fontSize: '0.7rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'hsl(var(--muted-foreground))',
  background: 'hsl(var(--muted))',
  letterSpacing: '0.05em',
};
const resultRowStyle = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '0.55rem 0.9rem',
  border: 'none',
  borderBottom: '1px solid hsl(var(--border))',
  background: 'transparent',
  cursor: 'pointer',
  fontSize: '0.85rem',
};
const subtleStyle = { fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: 2 };

// ---------------------------------------------------------------
// RevenueTab — MRR/ARR + plan mix + 90d signup series + cohorts + inactive
// ---------------------------------------------------------------
export function RevenueTab() {
  const [revenue, setRevenue] = useState(null);
  const [cohorts, setCohorts] = useState(null);
  const [inactive, setInactive] = useState(null);
  const [cohortsWindow, setCohortsWindow] = useState(30);
  const [inactiveDays, setInactiveDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [r, c, i] = await Promise.all([
          api.superAdminRevenue(),
          api.superAdminCohorts(cohortsWindow),
          api.superAdminInactiveOrgs(inactiveDays),
        ]);
        if (!alive) return;
        setRevenue(r); setCohorts(c); setInactive(i);
      } catch (err) {
        console.error(err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [cohortsWindow, inactiveDays]);

  if (loading && !revenue) return <div>Loading revenue…</div>;
  if (!revenue) return <div className="empty-state">Failed to load revenue analytics</div>;

  return (
    <div data-testid="revenue-tab-root">
      <div className="page-header">
        <h1>📈 Revenue & Growth</h1>
        <p>MRR, ARR, conversion funnels, and retention risk</p>
      </div>

      {/* Headline KPIs */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem',
        }}
      >
        <div className="stat-card" data-testid="kpi-mrr">
          <div className="stat-label">MRR</div>
          <div className="stat-value">£{(revenue.mrr || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</div>
        </div>
        <div className="stat-card" data-testid="kpi-arr">
          <div className="stat-label">ARR (12× MRR)</div>
          <div className="stat-value">£{(revenue.arr || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}</div>
        </div>
        <div className="stat-card" data-testid="kpi-active">
          <div className="stat-label">Active orgs</div>
          <div className="stat-value">{sumCounts(revenue.plan_counts?.active)}</div>
        </div>
        <div className="stat-card" data-testid="kpi-trialing">
          <div className="stat-label">Trialing</div>
          <div className="stat-value">{sumCounts(revenue.plan_counts?.trialing)}</div>
        </div>
        <div className="stat-card" data-testid="kpi-cancelled">
          <div className="stat-label">Cancelled / suspended</div>
          <div className="stat-value">{sumCounts(revenue.plan_counts?.cancelled)}</div>
        </div>
      </div>

      {/* Plan mix + MRR by plan */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <div className="card" data-testid="card-plan-mrr">
          <h3 style={{ marginTop: 0 }}>💰 MRR by plan</h3>
          {Object.keys(revenue.mrr_by_plan || {}).length === 0 ? (
            <div className="empty-state">No paid or trialing orgs yet</div>
          ) : (
            <table style={{ width: '100%', fontSize: '0.9rem' }}>
              <tbody>
                {Object.entries(revenue.mrr_by_plan).map(([plan, mrr]) => (
                  <tr key={plan} style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                    <td style={{ padding: '0.4rem 0.2rem', textTransform: 'capitalize' }}>{plan}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>£{mrr.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card" data-testid="card-plan-counts">
          <h3 style={{ marginTop: 0 }}>📦 Plan distribution</h3>
          {['active', 'trialing', 'cancelled'].map((bucket) => {
            const counts = revenue.plan_counts?.[bucket] || {};
            const keys = Object.keys(counts);
            if (keys.length === 0) return null;
            return (
              <div key={bucket} style={{ marginBottom: '0.75rem' }}>
                <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'hsl(var(--muted-foreground))', fontWeight: 700 }}>{bucket}</div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: 4 }}>
                  {keys.map((k) => (
                    <span key={k} className="badge" style={badgeStyle}>
                      {k} · <strong>{counts[k]}</strong>
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 90d signup line chart */}
      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="card-signup-series">
        <h3 style={{ marginTop: 0 }}>📅 New nurseries — last 90 days</h3>
        <MiniLineChart data={revenue.signup_series_90d || []} accessor={(d) => d.count} labelAccessor={(d) => d.date} />
      </div>

      {/* Cohort funnel */}
      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="card-cohorts">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h3 style={{ margin: 0 }}>🔄 Conversion funnel</h3>
          <select
            value={cohortsWindow}
            onChange={(e) => setCohortsWindow(Number(e.target.value))}
            data-testid="cohorts-window-select"
            style={{ padding: '0.35rem 0.6rem' }}
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
        {cohorts ? <FunnelBars cohorts={cohorts} /> : <div>Loading funnel…</div>}
      </div>

      {/* Inactive orgs */}
      <div className="card" data-testid="card-inactive-orgs">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h3 style={{ margin: 0 }}>🥶 Inactive organisations</h3>
          <select
            value={inactiveDays}
            onChange={(e) => setInactiveDays(Number(e.target.value))}
            data-testid="inactive-days-select"
            style={{ padding: '0.35rem 0.6rem' }}
          >
            <option value={7}>No login in 7d</option>
            <option value={14}>No login in 14d</option>
            <option value={30}>No login in 30d</option>
            <option value={60}>No login in 60d</option>
            <option value={90}>No login in 90d</option>
          </select>
        </div>
        {!inactive ? <div>Loading…</div> : inactive.count === 0 ? (
          <div className="empty-state">🎉 Every owner logged in recently.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid hsl(var(--border))', textAlign: 'left' }}>
                  <th style={{ padding: '0.4rem 0.5rem' }}>Nursery</th>
                  <th style={{ padding: '0.4rem 0.5rem' }}>Owner</th>
                  <th style={{ padding: '0.4rem 0.5rem' }}>Last login</th>
                  <th style={{ padding: '0.4rem 0.5rem' }}>Signups</th>
                </tr>
              </thead>
              <tbody data-testid="inactive-org-tbody">
                {inactive.orgs.map((o) => (
                  <tr key={o.organization_id} style={{ borderBottom: '1px solid hsl(var(--border))' }} data-testid={`inactive-row-${o.organization_id}`}>
                    <td style={{ padding: '0.4rem 0.5rem' }}>
                      <div style={{ fontWeight: 600 }}>{o.name}</div>
                      <div style={subtleStyle}><code>{o.slug}</code></div>
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>{o.owner_email}</td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>{fmtDate(o.last_login_at) || <span style={{ color: 'hsl(0 60% 50%)' }}>never</span>}</td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>{fmtDate(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function sumCounts(map) {
  return Object.values(map || {}).reduce((a, b) => a + b, 0);
}

function FunnelBars({ cohorts }) {
  const steps = [
    { label: 'Signed up', count: cohorts.signup, pct: 100 },
    { label: 'Onboarded (has child)', count: cohorts.onboarded, pct: cohorts.conversion?.onboarded ?? 0 },
    { label: 'Trialing', count: cohorts.trialing, pct: cohorts.conversion?.trialing ?? 0 },
    { label: 'Paid', count: cohorts.paid, pct: cohorts.conversion?.paid ?? 0 },
  ];
  const max = cohorts.signup || 1;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {steps.map((s) => (
        <div key={s.label} data-testid={`funnel-step-${s.label.split(' ')[0].toLowerCase()}`}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 2 }}>
            <span>{s.label}</span>
            <span><strong>{s.count}</strong> ({s.pct.toFixed(1)}%)</span>
          </div>
          <div style={{ height: 10, background: 'hsl(var(--muted))', borderRadius: 999 }}>
            <div style={{ width: `${Math.min(100, (s.count / max) * 100)}%`, height: '100%', background: 'hsl(var(--primary))', borderRadius: 999 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function MiniLineChart({ data, accessor, labelAccessor }) {
  if (!data || data.length === 0) return <div className="empty-state">No data</div>;
  const width = 800;
  const height = 140;
  const pad = 24;
  const values = data.map(accessor);
  const max = Math.max(1, ...values);
  const stepX = (width - pad * 2) / Math.max(1, data.length - 1);
  const points = data.map((d, i) => {
    const x = pad + i * stepX;
    const y = height - pad - (accessor(d) / max) * (height - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const totalSignups = values.reduce((a, b) => a + b, 0);
  const peak = data[values.indexOf(max)];
  return (
    <div>
      <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
        <div><strong>{totalSignups}</strong> new nurseries in the last 90d</div>
        <div>Peak day: <strong>{max}</strong> ({labelAccessor(peak)})</div>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 140 }}>
        <polyline
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth="2"
          points={points}
        />
        <polyline
          fill="hsl(var(--primary) / 0.12)"
          stroke="none"
          points={`${pad},${height - pad} ${points} ${width - pad},${height - pad}`}
        />
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------
// AuditLogTab
// ---------------------------------------------------------------
export function AuditLogTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [actions, setActions] = useState([]);
  const [filters, setFilters] = useState({ orgId: '', actor: '', action: '', fromTs: '', toTs: '' });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (nextPage = page) => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([
        api.superAdminAuditLog({ ...filters, page: nextPage, pageSize }),
        actions.length ? Promise.resolve({ actions }) : api.superAdminAuditActions(),
      ]);
      setRows(r.items || []);
      setTotal(r.total || 0);
      setPage(r.page || 1);
      setActions(a.actions || actions);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize, actions]);

  useEffect(() => { load(1); /* eslint-disable-next-line */ }, []);

  return (
    <div data-testid="audit-log-tab-root">
      <div className="page-header">
        <h1>📜 Audit Log</h1>
        <p>Every super-admin action across the platform</p>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', alignItems: 'end' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Org ID</label>
            <input value={filters.orgId} onChange={(e) => setFilters({ ...filters, orgId: e.target.value })} data-testid="audit-filter-org" placeholder="org_..." />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Actor email</label>
            <input value={filters.actor} onChange={(e) => setFilters({ ...filters, actor: e.target.value })} data-testid="audit-filter-actor" placeholder="you@..." />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Action</label>
            <select value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} data-testid="audit-filter-action">
              <option value="">All actions</option>
              {actions.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label>From (UTC)</label>
            <input type="datetime-local" value={filters.fromTs} onChange={(e) => setFilters({ ...filters, fromTs: e.target.value })} data-testid="audit-filter-from" />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label>To (UTC)</label>
            <input type="datetime-local" value={filters.toTs} onChange={(e) => setFilters({ ...filters, toTs: e.target.value })} data-testid="audit-filter-to" />
          </div>
          <button className="btn btn-primary" onClick={() => load(1)} data-testid="audit-apply-filters">🔎 Apply</button>
        </div>
      </div>

      <div className="card">
        {loading ? <div>Loading audit entries…</div> : rows.length === 0 ? (
          <div className="empty-state">No entries match these filters</div>
        ) : (
          <>
            <div style={{ marginBottom: '0.5rem', fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
              {total.toLocaleString()} entries total · page {page}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: '0.83rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid hsl(var(--border))', textAlign: 'left' }}>
                    <th style={{ padding: '0.4rem 0.5rem' }}>When (UTC)</th>
                    <th style={{ padding: '0.4rem 0.5rem' }}>Action</th>
                    <th style={{ padding: '0.4rem 0.5rem' }}>Actor</th>
                    <th style={{ padding: '0.4rem 0.5rem' }}>Target</th>
                    <th style={{ padding: '0.4rem 0.5rem' }}>Meta</th>
                  </tr>
                </thead>
                <tbody data-testid="audit-tbody">
                  {rows.map((r) => (
                    <tr key={r._id} style={{ borderBottom: '1px solid hsl(var(--border))' }} data-testid={`audit-row-${r._id}`}>
                      <td style={{ padding: '0.4rem 0.5rem', whiteSpace: 'nowrap' }}>{fmtDate(r.created_at)}</td>
                      <td style={{ padding: '0.4rem 0.5rem' }}><code>{r.action}</code></td>
                      <td style={{ padding: '0.4rem 0.5rem' }}>{r.actor_email || <span style={subtleStyle}>—</span>}</td>
                      <td style={{ padding: '0.4rem 0.5rem' }}>
                        {r.target_email && <div>{r.target_email}</div>}
                        {r.target_org_id && <div style={subtleStyle}><code>{r.target_org_id}</code></div>}
                      </td>
                      <td style={{ padding: '0.4rem 0.5rem', maxWidth: 320 }}>
                        <pre style={{ margin: 0, fontSize: '0.72rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                          {r.meta && Object.keys(r.meta).length ? JSON.stringify(r.meta) : '—'}
                        </pre>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', justifyContent: 'center' }}>
              <button className="btn btn-ghost" disabled={page <= 1} onClick={() => load(page - 1)} data-testid="audit-prev-page">← Prev</button>
              <span style={{ padding: '0.5rem 0.75rem', fontSize: '0.85rem' }}>
                Page {page} of {Math.max(1, Math.ceil(total / pageSize))}
              </span>
              <button className="btn btn-ghost" disabled={page * pageSize >= total} onClick={() => load(page + 1)} data-testid="audit-next-page">Next →</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// EmailHealthTab
// ---------------------------------------------------------------
export function EmailHealthTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.superAdminEmailHealth(days);
        if (alive) setData(r);
      } catch (err) {
        console.error(err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [days]);

  if (loading && !data) return <div>Loading email health…</div>;
  if (!data) return <div className="empty-state">Failed to load email health</div>;

  const healthy = data.success_rate >= 95;
  return (
    <div data-testid="email-health-tab-root">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1>📧 Email Deliverability</h1>
          <p>SMTP send success across the whole platform</p>
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="email-health-days" style={{ padding: '0.4rem 0.7rem' }}>
          <option value={1}>Last 24h</option>
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem',
        }}
      >
        <div className="stat-card" data-testid="email-kpi-success">
          <div className="stat-label">Success rate</div>
          <div className="stat-value" style={{ color: healthy ? 'hsl(140 60% 40%)' : 'hsl(0 65% 50%)' }}>
            {data.success_rate}%
          </div>
        </div>
        <div className="stat-card" data-testid="email-kpi-total"><div className="stat-label">Total attempts</div><div className="stat-value">{data.total}</div></div>
        <div className="stat-card" data-testid="email-kpi-sent"><div className="stat-label">Sent</div><div className="stat-value">{data.sent}</div></div>
        <div className="stat-card" data-testid="email-kpi-failed"><div className="stat-label">Failed</div><div className="stat-value">{data.failed}</div></div>
        <div className="stat-card" data-testid="email-kpi-skipped"><div className="stat-label">Skipped</div><div className="stat-value">{data.skipped}</div></div>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }} data-testid="email-series-card">
        <h3 style={{ marginTop: 0 }}>Send volume</h3>
        <MiniLineChart data={data.series || []} accessor={(d) => d.total} labelAccessor={(d) => d.date} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1rem' }}>
        <div className="card" data-testid="email-top-failures">
          <h3 style={{ marginTop: 0 }}>🚨 Top failure reasons</h3>
          {data.top_failures?.length ? (
            <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.85rem' }}>
              {data.top_failures.map((r, i) => (
                <li key={i} style={{ marginBottom: '0.4rem' }}>
                  <strong>×{r.count}</strong> — <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.reason}</span>
                </li>
              ))}
            </ul>
          ) : <div className="empty-state">No failures 🎉</div>}
        </div>
        <div className="card" data-testid="email-top-recipients">
          <h3 style={{ marginTop: 0 }}>📮 Top failing recipients</h3>
          {data.top_failing_recipients?.length ? (
            <table style={{ width: '100%', fontSize: '0.85rem' }}>
              <tbody>
                {data.top_failing_recipients.map((r) => (
                  <tr key={r.to} style={{ borderBottom: '1px solid hsl(var(--border))' }}>
                    <td style={{ padding: '0.4rem 0.2rem' }}>{r.to}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>×{r.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="empty-state">Clean sheet ✅</div>}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------
// Org detail panels: billing / usage / quick actions
// ---------------------------------------------------------------
export function OrgBillingPanel({ orgId }) {
  const [billing, setBilling] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try {
      const b = await api.superAdminOrgBilling(orgId);
      setBilling(b);
    } catch (err) {
      setMsg(err.message || 'Failed to load billing');
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  if (!billing) return <div className="card" data-testid="billing-panel-loading">Loading billing…</div>;
  const sub = billing.subscription || {};

  const extendTrial = async (days) => {
    if (!window.confirm(`Extend trial by ${days} days?`)) return;
    setBusy(true); setMsg('');
    try {
      await api.superAdminExtendTrial(orgId, days);
      setMsg(`Trial extended by ${days} days.`);
      await load();
    } catch (err) {
      setMsg(err.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const changePlan = async (newPlan) => {
    if (!window.confirm(`Change plan to '${newPlan}'?`)) return;
    setBusy(true); setMsg('');
    try {
      await api.superAdminSetSubscription(orgId, { plan: newPlan });
      setMsg(`Plan changed to ${newPlan}.`);
      await load();
    } catch (err) {
      setMsg(err.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const changeInterval = async (newInterval) => {
    setBusy(true); setMsg('');
    try {
      await api.superAdminSetSubscription(orgId, { billing_interval: newInterval });
      setMsg(`Billing interval set to ${newInterval}.`);
      await load();
    } catch (err) {
      setMsg(err.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (newStatus) => {
    if (!window.confirm(`Set subscription status to '${newStatus}'?`)) return;
    setBusy(true); setMsg('');
    try {
      await api.superAdminSetSubscription(orgId, { status: newStatus });
      setMsg(`Status set to ${newStatus}.`);
      await load();
    } catch (err) {
      setMsg(err.message || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: '1rem' }} data-testid="billing-panel">
      <h3 style={{ marginTop: 0 }}>💰 Billing & Subscription</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
        <Stat label="Plan" value={<code>{sub.plan || '—'}</code>} />
        <Stat label="Status" value={<StatusBadge status={sub.status} />} />
        <Stat label="Interval" value={sub.billing_interval || '—'} />
        <Stat label="Monthly" value={`£${(sub.monthly_price || 0).toFixed(2)}`} />
        <Stat label="MRR contrib." value={`£${(billing.mrr_contribution || 0).toFixed(2)}`} />
        <Stat label="Trial ends" value={fmtDate(sub.trial_ends_at) || '—'} />
        <Stat label="Days left" value={sub.trial_days_left ?? '—'} />
        <Stat label="Invoices (unpaid/total)" value={`${billing.invoices?.unpaid ?? 0} / ${billing.invoices?.total ?? 0}`} />
        <Stat label="Mollie payments" value={billing.payments?.total ?? 0} />
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', paddingTop: '0.5rem', borderTop: '1px solid hsl(var(--border))' }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label>Change plan</label>
          <select disabled={busy} value={sub.plan || ''} onChange={(e) => changePlan(e.target.value)} data-testid="billing-plan-select">
            <option value="starter">Starter</option>
            <option value="growth">Growth</option>
            <option value="professional">Professional</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label>Interval</label>
          <select disabled={busy} value={sub.billing_interval || 'monthly'} onChange={(e) => changeInterval(e.target.value)} data-testid="billing-interval-select">
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
          </select>
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label>Status</label>
          <select disabled={busy} value={sub.status || 'trialing'} onChange={(e) => changeStatus(e.target.value)} data-testid="billing-status-select">
            <option value="trialing">Trialing</option>
            <option value="active">Active</option>
            <option value="past_due">Past due</option>
            <option value="cancelled">Cancelled</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'end' }}>
          <button className="btn btn-ghost" disabled={busy} onClick={() => extendTrial(7)} data-testid="extend-trial-7">+7 days trial</button>
          <button className="btn btn-ghost" disabled={busy} onClick={() => extendTrial(14)} data-testid="extend-trial-14">+14 days</button>
          <button className="btn btn-ghost" disabled={busy} onClick={() => extendTrial(30)} data-testid="extend-trial-30">+30 days</button>
        </div>
      </div>
      {msg && <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'hsl(140 60% 40%)' }} data-testid="billing-msg">{msg}</div>}
    </div>
  );
}

export function OrgUsagePanel({ orgId }) {
  const [usage, setUsage] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const u = await api.superAdminOrgUsage(orgId);
        if (alive) setUsage(u);
      } catch (err) { console.error(err); }
    })();
    return () => { alive = false; };
  }, [orgId]);

  if (!usage) return null;
  return (
    <div className="card" style={{ marginBottom: '1rem' }} data-testid="usage-panel">
      <h3 style={{ marginTop: 0 }}>📊 Usage</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem' }}>
        {Object.entries(usage.counts || {}).map(([k, v]) => (
          <Stat key={k} label={k[0].toUpperCase() + k.slice(1)} value={v} />
        ))}
        <Stat label="Storage" value={usage.storage_human} />
      </div>
    </div>
  );
}

export function OrgQuickActionsBar({ orgId, ownerUser, onDone }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const resendWelcome = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await api.superAdminResendOrgWelcome(orgId);
      setMsg(r.ok ? `✅ Welcome email queued to ${r.sent_to}` : `⚠️ ${r.error || 'Failed to send'}`);
    } catch (err) {
      setMsg(`❌ ${err.message}`);
    } finally {
      setBusy(false);
      onDone && onDone();
    }
  };

  const impersonate = async () => {
    if (!ownerUser?._id) { setMsg('No owner user linked'); return; }
    if (!window.confirm(`Log in as ${ownerUser.email}? Your super-admin session will be preserved so you can exit cleanly.`)) return;
    try {
      await startImpersonation(ownerUser._id);
    } catch (err) {
      setMsg(`❌ ${err.message}`);
    }
  };

  return (
    <div className="card" style={{ marginBottom: '1rem' }} data-testid="quick-actions-bar">
      <h3 style={{ marginTop: 0 }}>⚡ Quick actions</h3>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button className="btn btn-primary" disabled={busy || !ownerUser} onClick={impersonate} data-testid="btn-impersonate-owner">
          🎭 Log in as owner
        </button>
        <button className="btn btn-ghost" disabled={busy} onClick={resendWelcome} data-testid="btn-resend-welcome">
          📨 Resend welcome email
        </button>
      </div>
      {msg && <div style={{ marginTop: '0.5rem', fontSize: '0.85rem' }} data-testid="quick-actions-msg">{msg}</div>}
    </div>
  );
}

// ---------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------
function Stat({ label, value }) {
  return (
    <div style={{ padding: '0.6rem 0.75rem', background: 'hsl(var(--muted))', borderRadius: 'var(--radius)' }}>
      <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: 'hsl(var(--muted-foreground))', fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: '1.05rem', fontWeight: 600, marginTop: 2 }}>{value ?? '—'}</div>
    </div>
  );
}

const badgeStyle = {
  padding: '0.25rem 0.6rem',
  fontSize: '0.75rem',
  background: 'hsl(var(--muted))',
  color: 'hsl(var(--foreground))',
  borderRadius: 999,
  border: '1px solid hsl(var(--border))',
};

function StatusBadge({ status }) {
  const map = {
    active: { color: 'hsl(140 60% 40%)', label: 'ACTIVE' },
    trialing: { color: 'hsl(210 80% 55%)', label: 'TRIALING' },
    past_due: { color: 'hsl(35 90% 50%)', label: 'PAST DUE' },
    cancelled: { color: 'hsl(0 65% 50%)', label: 'CANCELLED' },
    suspended: { color: 'hsl(0 65% 50%)', label: 'SUSPENDED' },
  };
  const s = map[status] || { color: 'hsl(var(--muted-foreground))', label: (status || 'unknown').toUpperCase() };
  return (
    <span style={{
      background: `${s.color}20`, color: s.color, padding: '0.15rem 0.5rem',
      borderRadius: 6, fontSize: '0.72rem', fontWeight: 700,
    }}>{s.label}</span>
  );
}

function fmtDate(v) {
  if (!v) return null;
  try {
    const d = typeof v === 'string' ? new Date(v) : v;
    if (isNaN(d.getTime())) return null;
    return d.toISOString().replace('T', ' ').slice(0, 16);
  } catch (_) { return null; }
}
